import Styles from "./style.module.scss";
import React from "react";
import { Field, useFormikContext } from "formik";
import { IPacketInOrder, ISessionRequestFields } from "../../../../types";
import { MyTextField } from "../../../../components/MyTextField";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { useNavigate } from "react-router-dom";
import { FormikFormComponent } from "../../../../components/FormikFormComponent";
import { newOrder } from "../../../../redux/Reducers/orderSlice";
import { validationSchemaOrderForm } from "../../../../validationForms";
import { BackLink } from "../../../../components/BackLink";
import { handleOrderSuccessfullySent } from "../../../../redux/Reducers/orderSlice";
import {
  clearSessionRequestConfirmation,
  saveSessionRequestConfirmation,
} from "../../../../utils/sessionRequestConfirmation";

const NewbornDateField: React.FC = () => {
  const { values } = useFormikContext<ISessionRequestFields>();
  const dateLabel = values.babyArrived ? "Baby’s birth date *" : "Baby’s due date *";

  return (
    <div className={Styles.formOrder__newbornFields}>
      <MyTextField
        nameLabel={dateLabel}
        type="date"
        name="newbornDate"
        component="input"
        id="newbornDate"
        editStyleContainer="dateOrder"
        editStyleField="order"
      />
      <label className={Styles.formOrder__babyArrived}>
        <Field type="checkbox" name="babyArrived" />
        <span>My baby has already arrived</span>
      </label>
      <p className={Styles.formOrder__dateHelp}>
        {values.babyArrived
          ? "The birth date helps me understand your baby’s current age."
          : "Enter your baby’s estimated due date."}
      </p>
    </div>
  );
};

export const FormOrder: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { packetInBasket } = useAppSelector((state) => state.packets);
  const { user } = useAppSelector((state) => state.user);
  const { loading, error } = useAppSelector((state) => state.order);
  const hasNewbornRequest = packetInBasket.some((packet) => packet.photosessionType.toLowerCase().includes("newborn"));
  const packetsRequiringTimeframe = packetInBasket.filter(
    (packet) => !packet.photosessionType.toLowerCase().includes("newborn")
  );
  const estimatedTotal = packetInBasket.reduce(
    (total, packet) => total + (Number.parseFloat(packet.price.replace(/[^0-9.]/g, "")) || 0),
    0
  );

  const initialValues: ISessionRequestFields = {
    babyArrived: false,
    name: user.name,
    email: user.email,
    phone: user && user.phone ? user.phone : "",
    newbornDate: "",
    packageTimeframes: packetsRequiringTimeframe.reduce<Record<string, string>>((timeframes, packet) => {
      timeframes[packet._id] = "";
      return timeframes;
    }, {}),
    text: "",
  };

  const orderNumber = (): string => {
    const dataOrder = new Date();
    const orderNumber = Math.random().toString(16).slice(2);
    return `${dataOrder.getFullYear()}-${dataOrder.getMonth()}-${dataOrder.getDate()}-${orderNumber}`;
  };

  const handleSubmit = async (values: ISessionRequestFields): Promise<boolean> => {
    clearSessionRequestConfirmation();
    const requestOrderNumber = orderNumber();
    const packetsInOrder = packetInBasket.map((packet): IPacketInOrder => {
      const isNewbornPackage = packet.photosessionType.toLowerCase().includes("newborn");
      return {
        namePacket: packet.namePacket,
        photosessionType: packet.photosessionType,
        price: packet.price,
        requestedDate: isNewbornPackage ? values.newbornDate : values.packageTimeframes[packet._id],
        requestedDateLabel: isNewbornPackage
          ? values.babyArrived
            ? "Baby's birth date"
            : "Baby's due date"
          : "Preferred session date",
        link: "",
      };
    });
    const result = await dispatch(
      newOrder({
        orderNumber: requestOrderNumber,
        packets: packetsInOrder,
        name: values.name.trim(),
        email: values.email.trim(),
        phone: values.phone?.trim() || "",
        text: values.text?.trim() || "",
      })
    );

    if (newOrder.fulfilled.match(result)) {
      saveSessionRequestConfirmation({
        email: values.email.trim(),
        orderNumber: requestOrderNumber,
      });
      dispatch(handleOrderSuccessfullySent(true));
      navigate("/order-success/", { replace: true });
      return true;
    }

    return false;
  };

  return (
    <section className={Styles.formOrder}>
      <div className={Styles.formOrder__containerHeader}>
        <BackLink linkName="← Back to your selection" path="/basket/" />
        <h1 className={Styles.formOrder__formTitle}>Request your session</h1>
        <p className={Styles.formOrder__intro}>
          Tell me how to reach you. I’ll personally follow up to confirm availability, timing, and session details.
        </p>
      </div>

      <div className={Styles.formOrder__layout}>
        <div className={Styles.formOrder__formColumn}>
          {error.newOrder && (
            <div className={Styles.formOrder__errorAlert} role="alert" aria-live="assertive">
              <strong>We couldn’t confirm your request</strong>
              <p>{error.newOrder} Your information is still here.</p>
            </div>
          )}

          <FormikFormComponent
            initialValues={initialValues}
            validationSchema={validationSchemaOrderForm(
              hasNewbornRequest,
              packetsRequiringTimeframe.map((packet) => packet._id)
            )}
            onSubmit={handleSubmit}
            buttonProps={{
              style: "ping",
              title: loading.newOrder ? "Sending your request…" : "Send session request",
              editStyle: "buttonSubmitOrder",
              edit: true,
            }}
            styleForm="order"
            loading={loading.newOrder}
            submitDisabled={loading.newOrder}
          >
            <MyTextField
              nameLabel="Full name *"
              type="text"
              name="name"
              component="input"
              id="name"
              editStyleContainer="fullNameOrder"
              editStyleField="order"
              placeholder="First and last name"
            />
            <MyTextField
              nameLabel="Email *"
              type="email"
              name="email"
              component="input"
              id="email"
              editStyleContainer="emailOrder"
              editStyleField="order"
              placeholder="you@example.com"
            />
            <MyTextField
              nameLabel="Phone"
              type="tel"
              name="phone"
              component="input"
              id="phone"
              editStyleContainer="telOrder"
              editStyleField="order"
              placeholder="(704) 555-0123"
            />
            {hasNewbornRequest && <NewbornDateField />}
            {packetsRequiringTimeframe.map((packet) => (
              <MyTextField
                key={packet._id}
                nameLabel={`Preferred session date — ${packet.photosessionType}: ${packet.namePacket} *`}
                type="date"
                name={`packageTimeframes.${packet._id}`}
                component="input"
                id={`packageTimeframe-${packet._id}`}
                editStyleContainer="dateOrder"
                editStyleField="order"
              />
            ))}
            <MyTextField
              nameLabel="Anything you’d like me to know?"
              type="text"
              name="text"
              component="textarea"
              id="text"
              editStyleContainer="textOrder"
              editStyleField="textOrder"
              placeholder="Family details, questions, or anything else you’d like to share…"
            />
          </FormikFormComponent>
          <p className={Styles.formOrder__privacy}>
            By sending this request, you agree to be contacted about your photography session. Your information stays
            private.
          </p>
        </div>

        <aside className={Styles.formOrder__summary} aria-label="Your session request">
          <h2>Your request</h2>
          {packetInBasket.map((packet) => (
            <div className={Styles.formOrder__summaryPackage} key={packet._id}>
              <span>{packet.namePacket}</span>
              <strong>${packet.price}</strong>
            </div>
          ))}
          <div className={Styles.formOrder__summaryTotal}>
            <span>Estimated total</span>
            <strong>${estimatedTotal}</strong>
          </div>
          <p className={Styles.formOrder__noPayment}>
            <strong>No payment today.</strong>
            <span>I’ll confirm availability and next steps personally.</span>
          </p>
        </aside>
      </div>
    </section>
  );
};
